import { getApsAuthBase } from "../../auth/apsConfig";
import type { DmAdsRegion } from "../dataManagement/region";
import { encodeModelDerivativeUrn } from "./encodeUrn";

export type TranslationStatus =
  | "success"
  | "inprogress"
  | "pending"
  | "failed"
  | "timeout"
  | "missing";

interface ManifestMessage {
  type?: string;
  message?: string;
  code?: string;
}

interface ManifestResponse {
  status?: string;
  progress?: string;
  derivatives?: Array<{ messages?: ManifestMessage[] }>;
}

function describeManifestFailure(json: ManifestResponse): string {
  const messages = json.derivatives?.flatMap(d => d.messages ?? []) ?? [];
  const errors = messages.filter(
    m => m.type === "error" || (m.code && m.code !== "OK")
  );
  const detail =
    errors.map(m => m.message).find(Boolean) ??
    messages.map(m => m.message).find(Boolean);
  if (detail) return detail;
  return json.status === "timeout"
    ? "Translation timed out on Autodesk servers — try again later."
    : "Model translation failed — open the file in ACC Desktop Connector or Revit and re-upload.";
}

const POLL_MS = 4000;
const MAX_POLL_ATTEMPTS = 90;

export class ModelDerivativeService {
  constructor(
    private readonly ensureAccessToken: () => Promise<string | null>
  ) {}

  private async token(): Promise<string> {
    const t = await this.ensureAccessToken();
    if (!t) throw new Error("Not signed in — APS access token required.");
    return t;
  }

  private async authHeaders(
    region?: DmAdsRegion
  ): Promise<Record<string, string>> {
    const token = await this.token();
    const h: Record<string, string> = {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    };
    if (region) h["x-ads-region"] = region;
    return h;
  }

  private async fetchManifest(
    versionUrn: string,
    region?: DmAdsRegion
  ): Promise<{ status: TranslationStatus; json: ManifestResponse | null }> {
    const base = getApsAuthBase();
    const encoded = encodeModelDerivativeUrn(versionUrn);
    const res = await fetch(
      `${base}/modelderivative/v2/designdata/${encoded}/manifest`,
      { headers: await this.authHeaders(region) }
    );
    if (res.status === 404) return { status: "missing", json: null };
    if (!res.ok) {
      const body = await res.text().catch(() => "");
      throw new Error(
        `Model Derivative manifest ${res.status}: ${body || res.statusText}`
      );
    }
    const json = (await res.json()) as ManifestResponse;
    const raw = json.status ?? "pending";
    if (raw === "success") return { status: "success", json };
    if (raw === "failed" || raw === "timeout") return { status: raw, json };
    if (raw === "inprogress" || raw === "pending") return { status: raw, json };
    return { status: "pending", json };
  }

  async getManifestStatus(
    versionUrn: string,
    region?: DmAdsRegion
  ): Promise<TranslationStatus> {
    const { status } = await this.fetchManifest(versionUrn, region);
    return status;
  }

  private translationFailureMessage(
    status: "failed" | "timeout",
    json: ManifestResponse | null
  ): string {
    if (json) {
      return `Model translation ${status}: ${describeManifestFailure(json)}`;
    }
    return `Model translation ${status} — try again later or re-upload the Revit file in ACC.`;
  }

  async requestTranslation(
    versionUrn: string,
    region?: DmAdsRegion
  ): Promise<void> {
    const base = getApsAuthBase();
    const res = await fetch(`${base}/modelderivative/v2/designdata/job`, {
      method: "POST",
      headers: await this.authHeaders(region),
      body: JSON.stringify({
        input: { urn: encodeModelDerivativeUrn(versionUrn) },
        output: {
          formats: [
            {
              type: "svf",
              views: ["2d", "3d"],
              advanced: { "2dviews": "legacy", generateMasterViews: true },
            },
          ],
        },
      }),
    });
    if (!res.ok) {
      const body = await res.text().catch(() => "");
      throw new Error(
        `Model Derivative translation job ${res.status}: ${body || res.statusText}`
      );
    }
  }

  /** Poll until the version is viewable in Forge Viewer. */
  async ensureViewable(
    versionUrn: string,
    region?: DmAdsRegion,
    onProgress?: (message: string, percent?: number) => void
  ): Promise<void> {
    let manifest = await this.fetchManifest(versionUrn, region);
    let status = manifest.status;
    console.info(
      "[Vera:ACC] Manifest status:",
      status,
      versionUrn.slice(0, 48)
    );
    if (status === "failed" || status === "timeout") {
      throw new Error(this.translationFailureMessage(status, manifest.json));
    }
    if (status === "missing") {
      onProgress?.("Requesting model translation…", 18);
      try {
        await this.requestTranslation(versionUrn, region);
        console.info("[Vera:ACC] Translation job submitted");
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        if (!msg.includes("403")) throw err;
        throw new Error(
          `${msg} — ACC may still be translating this file; try again in a few minutes.`
        );
      }
      status = "pending";
    }

    let attempts = 0;
    while (status !== "success") {
      if (status === "failed" || status === "timeout") {
        throw new Error(this.translationFailureMessage(status, manifest.json));
      }
      const pct = 20 + Math.round((attempts / MAX_POLL_ATTEMPTS) * 35);
      onProgress?.(
        status === "inprogress" || status === "pending"
          ? "Translating model for viewing…"
          : "Waiting for model translation…",
        pct
      );
      if (attempts >= MAX_POLL_ATTEMPTS) {
        throw new Error("Model translation timed out — try again later.");
      }
      await new Promise(r => setTimeout(r, POLL_MS));
      manifest = await this.fetchManifest(versionUrn, region);
      status = manifest.status;
      attempts += 1;
      if (attempts % 5 === 0) {
        console.info(
          `[Vera:ACC] Translation poll ${attempts}/${MAX_POLL_ATTEMPTS}: ${status}`
        );
      }
    }
    onProgress?.("Model translation complete", 55);
  }
}
